import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Spin} from 'antd'
import {ACTUAL_HOURS_CELL_LABELS} from '../../constants'
import {cellKey, rowTotalOf} from '../../helpers/actualHours'
import {DASH, formatDecimal, formatInt} from '../../helpers/format'

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']

/**
 * Kích thước CỐ ĐỊNH của lưới.
 *
 * ⚠️ `ROW_HEIGHT` là HỢP ĐỒNG giữa JS và CSS: phép ảo hoá dòng tính chỉ số dòng bằng
 *    `scrollY / ROW_HEIGHT`, nên nếu CSS cho dòng cao hơn thì phần đệm trên/dưới sai và lưới nhảy
 *    khi cuộn. Vì thế chiều cao được đặt bằng `style` inline ngay trong file này — một nguồn duy
 *    nhất — chứ KHÔNG khai trong `index.scss`. Ô có `overflow: hidden` để không nội dung nào đẩy
 *    dòng cao lên được.
 */
const ROW_HEIGHT = 48
const ACTOR_WIDTH = 170
const DAY_WIDTH = 86
const TOTAL_WIDTH = 82

/**
 * Số dòng vẽ dư ngoài khung nhìn, mỗi đầu.
 *
 * Không có nó thì cuộn nhanh sẽ thấy dải trắng ở mép trước khi React kịp vẽ. 6 dòng ≈ 288px, đủ cho
 * một cú lăn chuột, và vẫn rẻ hơn hẳn 75 dòng.
 */
const OVERSCAN = 6

/**
 * `'2026-10-26'` → `Date` lúc nửa đêm GIỜ MÁY.
 *
 * ⚠️ KHÔNG dùng `new Date('2026-10-26')`: chuỗi chỉ có ngày được JS hiểu là nửa đêm **UTC**, nên ở
 *    mọi múi giờ ÂM nó lùi về hôm trước — nhãn cột hiện sai thứ, và cuối tuần bị tô lệch một ngày.
 *    Ở Việt Nam (+07) lỗi này không bao giờ lộ, mà người xem ở múi giờ khác lại chính là người cần
 *    nhãn nhất. Cùng loại bẫy đã ghi cho `timezoneLabel` ở `helpers/format.js`.
 */
const localDayOf = (day) => {
    const [year, month, date] = day.split('-').map(Number)

    return new Date(year, month - 1, date)
}

/** Nhãn cột: `T2` trên `26/10`. Hai dòng vì 31 cột không còn chỗ cho một dòng dài. */
const DayHead = ({day}) => {
    const date = localDayOf(day)
    const weekend = date.getDay() === 0 || date.getDay() === 6

    return (
        <div className={`VcHoursHead${weekend ? ' VcHoursHead--weekend' : ''}`}>
            <div className="VcHoursHead__dow">{WEEKDAYS[date.getDay()]}</div>
            <div className="VcHoursHead__date">{`${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`}</div>
        </div>
    )
}

DayHead.propTypes = {day: PropTypes.string.isRequired}

/** Số → chuỗi hiện trong ô. `null`/`undefined` = CHƯA NHẬP, khác hẳn `0` = đã khai không làm. */
const textOf = (value) => (value === null || value === undefined ? '' : String(value).replace('.', ','))

/**
 * MỘT ô của lưới.
 *
 * ⚠️ Dùng `<input>` TRẦN, không dùng `InputNumber` của antd. Đo 03/11/2026 trên dữ liệu thật
 *    (75 tài khoản × 31 ngày = 2.325 ô): mỗi `InputNumber` tốn **12 node DOM + 2 SVG** (cặp mũi tên
 *    tăng/giảm), `<input>` trần tốn **1**. Cả lưới: 35.379 node · 4.650 SVG · heap jsdom +197 MB.
 *    Bỏ `InputNumber` cắt 25.575 node — 72% cây DOM — mà KHÔNG mất ràng buộc nào: `stepHint` của
 *    service chỉ là gợi ý bước nhảy cho mũi tên, còn `putHours` chỉ đòi số phút nguyên.
 *
 * ⚠️ Ô giữ TRẠNG THÁI CHỮ của riêng nó khi đang gõ, và đây không phải chuyện gọn gàng.
 *    `onEdit` đẩy giá trị đã PARSE lên cha; nếu ô là input thuần controlled theo con số đó thì gõ
 *    `7,` sẽ thành `7` ngay tại phím đó và dấu phẩy biến mất — người dùng KHÔNG BAO GIỜ gõ nổi
 *    `7,5`. Mà ở xưởng người ta gõ dấu phẩy (`parseHoursInput` nhận cả hai dấu, cả màn hình hiện
 *    số theo vi-VN). Hiệu ứng đồng bộ vì thế BỎ QUA khi con trỏ đang ở trong chính ô này.
 *
 * ⚠️ `React.memo` ở đây là vì HIỆU NĂNG, không phải gọn: gõ một phím hay bật cờ `loading` cũng làm
 *    cha vẽ lại, và nếu ô không memo thì React so lại toàn bộ ô đang hiện. Props vì thế PHẢI là
 *    giá trị nguyên thuỷ + một `onEdit` ỔN ĐỊNH — truyền nguyên `edits`/`errors` vào đây là memo
 *    thành vô dụng, im lặng, không lỗi nào báo.
 */
const HoursCell = React.memo(function HoursCell({actor, day, value, output, status, error, edited, readOnly, rejected, onEdit}) {
    /*
     * "Tương lai" lấy từ `status` của SERVICE, không so với đồng hồ máy người xem. Service cắt ngày
     * bằng UTC+7 cố định; máy người xem có thể ở múi giờ khác và đang ở một ngày lịch khác — khi đó
     * ô của hôm nay bị khoá hoặc ô của ngày mai mở ra nhận giờ rồi ăn 400.
     */
    const future = status === 'future'
    const inputRef = useRef(null)
    /*
     * ⚠️ `rejected` đi TRƯỚC `value` lúc khởi tạo. Dòng cuộn ra khỏi khung nhìn là UNMOUNT (ảo hoá),
     *    nên nếu chỉ khởi tạo từ `value` thì cuộn trở lại sẽ thấy một con số HỢP LỆ kèm viền đỏ —
     *    chữ người dùng thật sự gõ đã mất. Cha giữ nó lại chính vì lý do đó. (Vòng review 2.)
     */
    const [text, setText] = useState(() => (rejected === undefined ? textOf(value) : rejected))

    /**
     * ⚠️ Phụ thuộc có `error` chứ không chỉ `value`, và đó là một bản vá chứ không phải thừa.
     *
     * Giá trị bị TỪ CHỐI không bao giờ vào `edits` (có chủ đích — xem `onEdit` ở `ActualHoursPage`),
     * nên `value` KHÔNG đổi và hiệu ứng chỉ bám `value` sẽ không bao giờ chạy lại. Hệ quả: gõ `99`
     * vào một ô (quá trần 24) rồi bấm "Huỷ thay đổi" → `errors` sạch nên viền đỏ và dòng lỗi biến
     * mất, còn ô thì vẫn hiện `99`. Người dùng nhìn một ô trông sạch sẽ chứa con số KHÔNG nằm
     * trong `edits` lẫn dữ liệu đã lưu, và bấm Lưu thì nó không được gửi đi.
     * (Vòng review 03/11/2026.)
     */
    useEffect(() => {
        // Xem docblock: đang gõ trong chính ô này thì để yên, nếu không dấu phẩy bị nuốt.
        if (inputRef.current && document.activeElement === inputRef.current) return

        setText(rejected === undefined ? textOf(value) : rejected)
    }, [value, error, rejected])

    const onChange = useCallback((e) => {
        setText(e.target.value)
        onEdit(actor, day, e.target.value)
    }, [onEdit, actor, day])

    const classes = ['VcHoursCell', `VcHoursCell--${status}`]

    if (edited) classes.push('VcHoursCell--edited')
    if (error) classes.push('VcHoursCell--invalid')

    return (
        <div className={classes.join(' ')}>
            {readOnly ? (
                <div className="VcHoursCell__readonly">
                    {value === null || value === undefined ? DASH : formatDecimal(value, 1)}
                </div>
            ) : (
                <input
                    ref={inputRef}
                    className="VcHoursInput"
                    type="text"
                    // Điện thoại/máy tính bảng ở xưởng mở thẳng bàn phím số, không phải bàn phím chữ.
                    inputMode="decimal"
                    value={text}
                    placeholder={future ? '' : '—'}
                    disabled={future}
                    title={future ? ACTUAL_HOURS_CELL_LABELS.future : undefined}
                    aria-label={`${actor} ${day}`}
                    maxLength={5}
                    onChange={onChange}
                />
            )}

            {/*
             * ⚠️ Dòng ghi chú LUÔN tồn tại, kể cả khi rỗng — nó giữ chiều cao ô không đổi, mà chiều
             *    cao không đổi chính là điều kiện để phép ảo hoá dòng tính đúng (`ROW_HEIGHT`).
             *    Dựng có điều kiện thì ô có lỗi cao hơn ô thường và lưới nhảy khi cuộn.
             *
             * Lỗi được ưu tiên hơn sản lượng: ô đang sai thì việc cần làm là sửa con số, không phải
             * đối chiếu. Cả hai đều là CHỮ nên ô vẫn không nói bằng mỗi màu (AC-UI-01).
             */}
            <div className="VcHoursCell__note">
                {error
                    ? <span className="VcHoursCell__error">{error}</span>
                    : output > 0 ? <span className="VcHoursCell__output">{formatInt(output)} sp</span> : null}
            </div>
        </div>
    )
})

HoursCell.propTypes = {
    actor: PropTypes.string.isRequired,
    day: PropTypes.string.isRequired,
    value: PropTypes.number,
    output: PropTypes.number,
    status: PropTypes.string,
    error: PropTypes.string,
    edited: PropTypes.bool,
    readOnly: PropTypes.bool,
    /** Chữ người dùng gõ mà bị TỪ CHỐI — cha giữ, vì dòng cuộn ra là unmount. */
    rejected: PropTypes.string,
    onEdit: PropTypes.func.isRequired,
}

const EMPTY_CELL = {hours: null, output: 0, status: 'idle'}

/**
 * Khoảng dòng cần VẼ, tính theo vị trí cuộn của TRANG.
 *
 * ⚠️ Bám `window` chứ KHÔNG dựng một khung cuộn dọc riêng cho lưới. Thanh cuộn dọc lồng trong trang
 *    là thứ màn này đã cố ý tránh từ trước (xem ghi chú `.VcStickyHead` ở `index.scss`) — nó làm
 *    người dùng phải đoán đang cuộn cái gì, và bánh xe chuột thì nhảy giữa hai khung.
 *
 * ⚠️ **`capture: true` là BẮT BUỘC, không phải tuỳ chọn.** Sự kiện `scroll` KHÔNG nổi bọt, nên một
 *    listener thường trên `window` chỉ nghe được khi chính TÀI LIỆU cuộn. Khung ứng dụng của webapp
 *    đặt `overflow: auto` lên `.content-wrapper`, nên tuỳ chiều cao nội dung mà khung cuộn có thể là
 *    phần tử ĐÓ chứ không phải tài liệu — khi ấy listener thường không bao giờ chạy, cửa sổ dòng
 *    đứng im ở lượt tính đầu tiên, và người dùng cuộn xuống chỉ thấy khoảng trắng đúng bằng chiều
 *    cao hai dòng đệm. Pha BẮT của `window` nhận được scroll từ MỌI phần tử con, nên cách này đúng
 *    cho cả hai kiểu bố cục mà không phải đoán xem ai đang cuộn.
 *
 * ⚠️ Chỉ `setState` khi khoảng THẬT SỰ đổi. Không có phép so đó thì mỗi sự kiện cuộn là một lượt
 *    vẽ lại cả lưới — đúng thứ đang muốn bỏ đi.
 */
const useRowWindow = (count, ref) => {
    const [range, setRange] = useState({first: 0, last: count, rowHeight: ROW_HEIGHT})
    /**
     * Chiều cao ĐÃ ĐO gần nhất.
     *
     * ⚠️ Không được rơi về hằng khi phép đo không có gì để đo. Cuộn qua hẳn bảng thì `visible` rỗng
     *    (cả `first` lẫn `last` kẹp về `count`), `querySelector` trả `null`, và nếu lúc đó lấy lại
     *    `ROW_HEIGHT` thì `padTop` của 75 dòng tụt từ 3.975px xuống 3.600px — mọi thứ bên dưới bảng
     *    NHẢY lên ~375px mà người dùng không cuộn gì; cuộn ngược lại thì đo được 53 và nó nhảy về.
     *    Gần biên, `ResizeObserver` mới thêm còn có thể làm hai trạng thái đó dội qua dội lại.
     *    (Vòng review 2.)
     */
    const measuredRef = useRef(ROW_HEIGHT)

    useLayoutEffect(() => {
        const recompute = () => {
            const el = ref.current

            if (!el) return

            /**
             * ⚠️ ĐO chiều cao dòng thật, không tin hằng số.
             *
             * `ROW_HEIGHT` chỉ là ước lượng ban đầu, và một hợp đồng JS↔CSS KHÔNG được kiểm ở đâu
             * cả thì sớm muộn cũng lệch: cộng đúng CSS hiện tại ra ~53px (ô 44 + đệm ô bảng 8 +
             * viền 1), trong khi `height` trên `<tr>` chỉ là mức TỐI THIỂU nên dòng vẫn cao 53.
             * Khi đó phần đệm tính theo 48 còn dòng thật tốn 53 — tổng chiều cao bảng đổi theo
             * từng lượt cuộn, và `firstVisible` lệch ~10%, tức sau khoảng mười dòng là cửa sổ đi
             * trước nội dung một dòng. Font khác, zoom khác hay sửa CSS đều gây lại đúng lỗi này.
             * Đo thì không có hợp đồng nào để vỡ. Test jsdom không bắt được (mọi số đo là 0), nên
             * đây là chỗ phải tự sửa được chứ không phải chỗ để khoá bằng test. (Vòng review
             * 03/11/2026.)
             */
            const sample = el.querySelector('tr[data-hours-row]')
            const measured = sample ? sample.getBoundingClientRect().height : 0

            if (measured > 0) measuredRef.current = measured

            const rowHeight = measuredRef.current

            const top = el.getBoundingClientRect().top
            const viewport = window.innerHeight || 0
            const firstVisible = Math.floor(-top / rowHeight)
            const fit = Math.ceil(viewport / rowHeight)
            const first = Math.max(0, Math.min(count, firstVisible - OVERSCAN))
            const last = Math.max(first, Math.min(count, firstVisible + fit + OVERSCAN))

            setRange((prev) =>
                prev.first === first && prev.last === last && prev.rowHeight === rowHeight
                    ? prev
                    : {first, last, rowHeight}
            )
        }

        recompute()
        window.addEventListener('scroll', recompute, {passive: true, capture: true})
        window.addEventListener('resize', recompute)

        /*
         * ⚠️ Lưới còn trôi khi nội dung PHÍA TRÊN nó xuất hiện/biến mất mà không có cú cuộn nào —
         *    cụ thể là dải nhắc "còn N ngày-công thiếu giờ" của `ActualHoursPage`, biến mất ngay
         *    khi người dùng gõ nốt ô cuối. Dải đó cao cỡ một dòng, nên sau đó cửa sổ lệch một dòng
         *    cho tới cú cuộn kế tiếp. `resize` của `window` không bắt được vì cửa sổ có đổi gì đâu.
         */
        /*
         * ⚠️ Bám CẢ `body` LẪN khối trang. Khi khung cuộn là `.content-wrapper` (xem ghi chú ở
         *    `useRowWindow`), `body` đứng yên ở chiều cao khung nhìn — nên dải nhắc biến mất không
         *    đổi gì mà `body` thấy được, và bản vá trở thành vô tác dụng đúng ở ca nó sinh ra để
         *    xử lý. Khối trang thì đổi chiều cao ở cả hai kiểu bố cục. (Vòng review 2.)
         */
        const observer =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(recompute)
        const page = ref.current && ref.current.closest ? ref.current.closest('.VcPage') : null

        if (observer) {
            if (document.body) observer.observe(document.body)
            if (page && page !== document.body) observer.observe(page)
        }

        return () => {
            window.removeEventListener('scroll', recompute, {capture: true})
            window.removeEventListener('resize', recompute)
            if (observer) observer.disconnect()
        }
    }, [count, ref])

    return range
}

/**
 * Một dòng của lưới.
 *
 * ⚠️ `memo` ở đây CHỈ ăn khi CUỘN, không ăn khi gõ phím — nói rõ ra vì bản comment trước hứa nhiều
 *    hơn thứ nó làm. `edits`/`errors` được truyền nguyên cả object, mà `setEdits((prev) => ({...prev}))`
 *    sinh identity mới sau mỗi ký tự, nên mọi dòng đang hiện đều dựng lại khi gõ — đúng cái bẫy
 *    docblock của `HoursCell` cảnh báo. Giữ `memo` vì đường NÓNG là cuộn: ở đó identity của
 *    `edits`/`errors` đứng yên và nó chặn được toàn bộ phần dòng không đổi.
 *
 *    Giá của phần không ăn là bị CHẶN TRẦN bởi `memo` của `HoursCell`: dựng lại một dòng chỉ là tạo
 *    ~31 element, còn cây con đắt tiền (ô nhập) thì không bị so lại. Cắt nốt phần đó cần cắt lát
 *    `edits`/`errors` theo từng dòng và memo hoá lát đó — nhiều máy móc hơn mức nó đáng.
 *    (Vòng review 03/11/2026.)
 */
const HoursRow = React.memo(function HoursRow({row, days, edits, errors, invalidText, onEdit, readOnly}) {
    return (
        <tr data-hours-row="" style={{height: ROW_HEIGHT}}>
            <td className="VcHoursTable__actor">
                <span className="VcHoursActor">{row.actor}</span>
            </td>

            {days.map((day) => {
                const cell = row.cells[day] || EMPTY_CELL
                const key = cellKey(row.actor, day)
                const edited = Object.prototype.hasOwnProperty.call(edits, key)

                return (
                    <td key={day}>
                        <HoursCell
                            actor={row.actor}
                            day={day}
                            value={edited ? edits[key] : cell.hours}
                            output={cell.output}
                            status={cell.status}
                            error={errors[key]}
                            edited={edited}
                            readOnly={readOnly}
                            rejected={invalidText[key]}
                            onEdit={onEdit}
                        />
                    </td>
                )
            })}

            {/*
             * Tổng tính LẠI ở client chứ không lấy `row.total` của service: service trả tổng của dữ
             * liệu ĐÃ LƯU, nên gõ 8 giờ vào ba ô mà cột này đứng im thì người dùng tưởng mình gõ hụt.
             */}
            <td className="VcHoursTable__total">
                {(() => {
                    const total = rowTotalOf(row, days, edits)

                    return <strong>{total === 0 ? DASH : formatDecimal(total, 1)}</strong>
                })()}
            </td>
        </tr>
    )
})

HoursRow.propTypes = {
    row: PropTypes.object.isRequired,
    days: PropTypes.arrayOf(PropTypes.string).isRequired,
    edits: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    invalidText: PropTypes.object.isRequired,
    onEdit: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
}

HoursRow.defaultProps = {readOnly: false}

/**
 * Lưới nhập giờ làm thực tế — tài khoản × ngày (FAC-26417 FR-001).
 *
 * Mỗi ô có HAI con số và chúng không cùng vai: số giờ là thứ người dùng NHẬP, còn sản lượng ngay
 * dưới là thứ chỉ để ĐỐI CHIẾU ("hôm đó có 47 sản phẩm thì không thể 0 giờ"). Sản lượng không tham
 * gia phép tính nào ở màn này.
 *
 * ⚠️ Màu KHÔNG phải kênh thông tin duy nhất (AC-UI-01). Ô thiếu giờ được tô vàng, nhưng LÝ DO nó
 *    vàng — con số sản lượng — luôn hiện thành chữ trong chính ô đó, nên người không phân biệt được
 *    màu vẫn đọc ra. Màn hình tại xưởng chất lượng thấp, đây không phải chuyện lý thuyết.
 *
 * ⚠️ **Bảng DỰNG TAY, không dùng `Table` của antd, và chỉ vẽ những dòng đang nhìn thấy.** Đo
 *    03/11/2026 trên dữ liệu thật (75 tài khoản × 31 ngày): bản antd vẽ **35.379 node DOM, 2.325
 *    input, 4.650 SVG**, heap jsdom +197 MB — tab trình duyệt báo ~861 MB. Hai nguyên nhân, cộng
 *    dồn: `InputNumber` (12 node/ô, xem `HoursCell`) và việc vẽ đủ 75 dòng trong khi màn hình chỉ
 *    thấy ~15. Bản này còn **2.124 node (−94%)**.
 *
 *    Giá phải trả — ghi ra để lần sau không ai "sửa lại cho gọn": antd `Table` không ảo hoá dòng ở
 *    4.5.4, và `components.body` của nó thì phá hai cột ghim. Ghim ở đây là `position: sticky` trên
 *    chính `<td>`, chạy được vì khung cuộn NGANG là phần tử bọc ngay bên ngoài `<table>`.
 */
function ActualHoursGrid({days, rows, edits, errors, invalidText, onEdit, readOnly, loading}) {
    const bodyRef = useRef(null)
    const {first, last, rowHeight} = useRowWindow(rows.length, bodyRef)
    const visible = useMemo(() => rows.slice(first, last), [rows, first, last])
    const width = ACTOR_WIDTH + days.length * DAY_WIDTH + TOTAL_WIDTH
    const padTop = first * rowHeight
    const padBottom = Math.max(0, rows.length - last) * rowHeight
    const colSpan = days.length + 2

    return (
        <Spin spinning={loading}>
            {/*
             * Khung cuộn NGANG. `table-layout: fixed` + `<colgroup>` để bề rộng cột không đổi theo
             * nội dung — thiếu nó thì mỗi lần cuộn, tập dòng đang vẽ đổi và cả bảng co giãn theo.
             */}
            <div className="VcHoursScroll">
                <table className="VcHoursTable" style={{width}}>
                    <colgroup>
                        <col style={{width: ACTOR_WIDTH}}/>
                        {days.map((day) => <col key={day} style={{width: DAY_WIDTH}}/>)}
                        <col style={{width: TOTAL_WIDTH}}/>
                    </colgroup>

                    <thead>
                        <tr>
                            <th className="VcHoursTable__actor">Tài khoản</th>
                            {days.map((day) => <th key={day}><DayHead day={day}/></th>)}
                            <th className="VcHoursTable__total">Tổng</th>
                        </tr>
                    </thead>

                    <tbody ref={bodyRef}>
                        {/*
                         * Hai dòng đệm giữ nguyên CHIỀU CAO thật của bảng, nên thanh cuộn của trang
                         * không đổi khi tập dòng đang vẽ đổi. Bỏ chúng đi là trang co lại và cú cuộn
                         * đang dở bị giật ngược.
                         */}
                        {padTop > 0 && (
                            <tr aria-hidden="true" style={{height: padTop}}>
                                <td colSpan={colSpan} className="VcHoursTable__pad"/>
                            </tr>
                        )}

                        {visible.map((row) => (
                            <HoursRow
                                key={row.actor}
                                row={row}
                                days={days}
                                edits={edits}
                                errors={errors}
                                invalidText={invalidText}
                                onEdit={onEdit}
                                readOnly={readOnly}
                            />
                        ))}

                        {padBottom > 0 && (
                            <tr aria-hidden="true" style={{height: padBottom}}>
                                <td colSpan={colSpan} className="VcHoursTable__pad"/>
                            </tr>
                        )}

                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={colSpan}>
                                    <div className="VcEmptyState">
                                        <div>Không có tài khoản nào trong khoảng này.</div>
                                        <div className="VcHint">
                                            Lưới chỉ hiện tài khoản CÓ sản lượng hoặc ĐÃ nhập giờ trong khoảng đang lọc.
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </Spin>
    )
}

ActualHoursGrid.propTypes = {
    days: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.array.isRequired,
    edits: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    invalidText: PropTypes.object,
    onEdit: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    loading: PropTypes.bool,
}

ActualHoursGrid.defaultProps = {readOnly: false, loading: false, invalidText: {}}

export {DayHead, localDayOf, HoursCell, ROW_HEIGHT, OVERSCAN}

export default ActualHoursGrid

/**
 * Chú thích màu của lưới — để cạnh bảng, không nhét vào tooltip.
 *
 * Bốn trạng thái mà chỉ một cái cần hành động; không có chú thích thì ô vàng trông như lỗi và người
 * dùng sẽ hỏi lại mỗi lần mở màn.
 */
export function HoursLegend() {
    return (
        <div className="VcHoursLegend">
            {['missing', 'filled', 'idle', 'future'].map((status) => (
                <span key={status} className={`VcHoursLegend__item VcHoursCell--${status}`}>
                    {ACTUAL_HOURS_CELL_LABELS[status]}
                </span>
            ))}
        </div>
    )
}
